import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fmt(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return `${Number(n).toFixed(2)} €`;
}

export default function AdminCartRequirements() {
    const { cartId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/carts/${cartId}/requirements`);
                setData(data);
            } catch (err) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Erro a carregar folha de requisitos");
            } finally {
                setLoading(false);
            }
        })();
    }, [cartId]);

    if (loading) return <div className="p-10 text-center text-muted-foreground">A carregar…</div>;
    if (!data) return <div className="p-10 text-center text-muted-foreground">Sem dados.</div>;

    async function downloadPdf() {
        try {
            const res = await api.get(`/admin/carts/${cartId}/requirements.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const serial = data?.cart?.serial_number || cartId;
            a.download = `NOVIFOOD-Requisitos-${serial}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro a gerar PDF");
        }
    }

    const eq = data.cart || {};
    const owner = data.owner || {};
    const sections = data.sections || [];
    const grand = data.grand_total_eur ?? 0;
    const generated = data.generated_at ? new Date(data.generated_at).toLocaleString("pt-PT") : "";

    return (
        <div className="min-h-screen bg-gray-100 py-6 print:bg-white print:py-0" data-testid="requirements-sheet">
            {/* Toolbar — hidden on print */}
            <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
                <Link to="/admin/equipments" className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline">
                    <ArrowLeft className="h-4 w-4" /> Voltar aos carrinhos
                </Link>
                <div className="flex items-center gap-2">
                    <button
                        onClick={downloadPdf}
                        data-testid="requirements-pdf-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-white text-primary px-4 py-2 text-sm font-bold hover:bg-muted transition"
                    >
                        <Download className="h-4 w-4" /> Descarregar PDF
                    </button>
                    <button
                        onClick={() => window.print()}
                        data-testid="requirements-print-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:-translate-y-0.5 transition"
                    >
                        <Printer className="h-4 w-4" /> Imprimir
                    </button>
                </div>
            </div>

            {/* Sheet */}
            <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none print:max-w-full">
                <div className="p-10 print:p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-6 border-b-2 border-primary pb-6">
                        <div>
                            <div className="text-3xl font-heading font-black tracking-tight text-primary">NOVIFOOD</div>
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                                Folha de Requisitos · Carrinho
                            </p>
                        </div>
                        <div className="text-right text-xs">
                            <div className="uppercase font-bold text-muted-foreground tracking-wider">Documento</div>
                            <div className="mt-1 font-mono text-primary">REQ-{(eq.serial_number || eq.id || "").toString().slice(-8).toUpperCase()}</div>
                            <div className="mt-2 uppercase font-bold text-muted-foreground tracking-wider">Emitido</div>
                            <div className="mt-1">{generated}</div>
                        </div>
                    </div>

                    {/* Cart + owner grid */}
                    <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Carrinho</div>
                            <div className="mt-1 font-bold text-primary">
                                {eq.nickname || eq.serial_number}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                                Modelo <span className="capitalize">{eq.model}</span> · Nº série <span className="font-mono">{eq.serial_number}</span>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                                Compra: {eq.purchase_date || "—"} · Garantia até: {eq.warranty_until || "—"} ({eq.warranty_months || 24}m)
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Cliente</div>
                            <div className="mt-1 font-bold text-primary">{owner.name || owner.email || "—"}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{owner.email || ""}</div>
                        </div>
                    </div>

                    {/* Sections */}
                    <div className="mt-8 space-y-6">
                        {sections.length === 0 && (
                            <div className="text-sm text-muted-foreground italic">Sem peças registadas neste carrinho.</div>
                        )}
                        {sections.map((sec) => (
                            <div key={sec.category || "none"} data-testid={`req-section-${sec.category || "none"}`}>
                                <div className="flex items-baseline justify-between border-b border-border pb-1">
                                    <h3 className="font-heading font-black uppercase tracking-wider text-primary text-sm">
                                        {sec.label}
                                    </h3>
                                    <span className="text-xs text-muted-foreground">
                                        {sec.items.length} {sec.items.length === 1 ? "linha" : "linhas"}
                                    </span>
                                </div>
                                <table className="mt-2 w-full text-xs">
                                    <thead>
                                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                            <th className="text-left py-1 w-24">Código</th>
                                            <th className="text-left py-1">Peça</th>
                                            <th className="text-left py-1 w-24">SKU</th>
                                            <th className="text-right py-1 w-14">Qtd</th>
                                            <th className="text-right py-1 w-20">Preço un.</th>
                                            <th className="text-right py-1 w-24">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/60">
                                        {sec.items.map((it) => (
                                            <tr key={it.id}>
                                                <td className="py-1.5 font-mono text-[10px]">{it.id}</td>
                                                <td className="py-1.5">
                                                    <div className="font-bold text-primary">{it.name}</div>
                                                    {it.supplier && (
                                                        <div className="text-[10px] text-muted-foreground">Fornecedor: {it.supplier}</div>
                                                    )}
                                                    {it.warranty_until && (
                                                        <div className="text-[10px] text-muted-foreground">
                                                            Garantia até {it.warranty_until}
                                                            {it.warranty_months ? ` · ${it.warranty_months}m` : " · (global)"}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-1.5 font-mono text-[10px]">{it.sku || "—"}</td>
                                                <td className="py-1.5 text-right">{it.quantity}</td>
                                                <td className="py-1.5 text-right">{fmt(it.unit_price_eur)}</td>
                                                <td className="py-1.5 text-right font-bold">{fmt(it.line_total_eur)}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td colSpan={5} className="pt-2 text-right text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                                Subtotal
                                            </td>
                                            <td className="pt-2 text-right font-bold text-primary">{fmt(sec.subtotal_eur)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>

                    {/* Grand total */}
                    <div className="mt-8 border-t-2 border-primary pt-4 flex items-baseline justify-between">
                        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                            Total do carrinho
                        </div>
                        <div className="text-3xl font-heading font-black text-primary" data-testid="req-grand-total">
                            {fmt(grand)}
                        </div>
                    </div>

                    <div className="mt-10 text-[10px] text-muted-foreground italic">
                        Documento interno gerado automaticamente pelo sistema NOVIFOOD.
                        Cada peça possui um código único (etiqueta com código de barras Code128) para gestão de garantia e stock.
                    </div>
                </div>
            </div>

            {/* Print styles */}
            <style>{`
                @media print {
                    body { background: white !important; }
                    @page { size: A4; margin: 12mm; }
                }
            `}</style>
        </div>
    );
}
